import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import axios from 'axios';
import axiosInstance from '../api/axiosInterceptor';
import '../styles/Login.css';
import SyncLoader from "../components/Loader";

const API_BASE = 'https://api.ambrishquickpatente.com/api';

const Login = () => {
  const pendingResetEmail = localStorage.getItem('pendingPasswordResetEmail') || '';
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [loginStage, setLoginStage] = useState('login');
  const [tempPasswordRequestedFor, setTempPasswordRequestedFor] = useState(pendingResetEmail);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false); 
  const navigate = useNavigate();

  useEffect(() => {
    if (pendingResetEmail && !username) {
      setUsername(pendingResetEmail);
    }
  }, [pendingResetEmail, username]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setStatusMessage('');
    setLoading(true);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_BASE}/login/`, {
        email: username,
        password: password,
        status: "admin-panel"
      });

      const { access, refresh, user_id, email, full_name, language, phone, profile_picture } = response.data;

      localStorage.setItem('accessToken', access);
      localStorage.setItem('refreshToken', refresh);
      localStorage.setItem('user', JSON.stringify({
        user_id,
        email,
        full_name,
        language,
        phone,
        profile_picture
      }));

      const pendingReset = localStorage.getItem('pendingPasswordResetEmail') || tempPasswordRequestedFor;
      const isTempLoginForEmail =
        pendingReset &&
        username.trim().toLowerCase() === pendingReset.trim().toLowerCase();
      const backendRequiresChange =
        response.data.must_change_password || response.data.force_password_change;

      if (pendingReset || isTempLoginForEmail || backendRequiresChange) {
        setLoginStage('change');
        setStatusMessage('Logged in with temporary password. Please set a new password.');
        return;
      }

      navigate('/dashboard/chapters');
    } catch (error) {
      console.error('Login failed:', error);
      setErrorMessage('Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setStatusMessage('');
    const requestEmail = (username || '').trim() || 'admin@gmail.com';

    setLoading(true);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API_BASE}/admin/forgot-password/`, {
        email: requestEmail,
      });

      setTempPasswordRequestedFor(requestEmail);
      localStorage.setItem('pendingPasswordResetEmail', requestEmail);
      setStatusMessage(
        response?.data?.success || 'Check email for temporary password'
      );
      setLoginStage('login');
      setUsername(requestEmail);
      setPassword('');
    } catch (error) {
      console.error('Forgot password failed:', error);
      setErrorMessage(
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        'Could not process forgot password request. Please try again.'
      );
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setStatusMessage('');

    if (!password) {
      setErrorMessage('Temporary password is required.');
      return;
    }

    if (!newPassword || !confirmNewPassword) {
      setErrorMessage('Please enter and confirm your new password.');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setErrorMessage('New password and confirm password do not match.');
      return;
    }

    setLoading(true);
    setIsLoading(true);

    try {
      await axiosInstance.post('/change-password/', {
        old_password: password,
        new_password: newPassword,
      });

      setTempPasswordRequestedFor('');
      localStorage.removeItem('pendingPasswordResetEmail');
      setStatusMessage('Password changed successfully. Redirecting...');
      navigate('/dashboard/chapters');
    } catch (error) {
      console.error('Change password failed:', error);
      setErrorMessage(
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        'Failed to change password. Please verify the temporary password and try again.'
      );
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      {isLoading && <SyncLoader message="Logging in"/>}
      <div className="login-container">
        <h2>
          {loginStage === 'forgot' && 'Forgot Password'}
          {loginStage === 'change' && 'Change Password'}
          {loginStage === 'login' && 'Login'}
        </h2>

        {statusMessage && <div className="auth-message success">{statusMessage}</div>}
        {errorMessage && <div className="auth-message error">{errorMessage}</div>}

        {loginStage === 'forgot' ? (
          <form onSubmit={handleForgotPassword}>
            <p className="helper-text">
              Click below to send a temporary password for <strong>{username || 'admin@gmail.com'}</strong>.
            </p>

            <button type="submit" disabled={loading}>
              {loading ? 'Sending...' : 'Send Temporary Password'}
            </button>

            <div className="auth-actions-row">
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setLoginStage('login');
                }}
              >
                Back to Login
              </button>
            </div>
          </form>
        ) : null}

        {loginStage === 'change' ? (
          <form onSubmit={handleChangePassword}>
            <div className="form-password">
              <label htmlFor="temp-password">Temporary Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="temp-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <span className="password-toggle-icon" onClick={togglePasswordVisibility}>
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
            </div>

            <div className="form-password">
              <label htmlFor="new-password">New Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  id="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
                <span
                  className="password-toggle-icon"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                >
                  {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
            </div>

            <div className="form-password">
              <label htmlFor="confirm-new-password">Confirm New Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showConfirmNewPassword ? 'text' : 'password'}
                  id="confirm-new-password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  required
                />
                <span
                  className="password-toggle-icon"
                  onClick={() => setShowConfirmNewPassword((prev) => !prev)}
                >
                  {showConfirmNewPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
            </div>

            <button type="submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        ) : null}

        {loginStage === 'login' ? (
          <form onSubmit={handleLogin}>
            <div className="form-username">
              <label htmlFor="username">Username (Email)</label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>

            <div className="form-password">
              <label htmlFor="password">Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <span className="password-toggle-icon" onClick={togglePasswordVisibility}>
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
            </div>

            <div className="form-remember">
              <div className="remember-checkbox">
                <input
                  type="checkbox"
                  id="remember"
                  name="remember"
                />
                <label htmlFor="remember">Remember me</label>
              </div>
              <div className="forgot-password">
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    setLoginStage('forgot');
                  }}
                >
                  Forgot Password?
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </button>

            <p className="privacy-link" style={{ marginTop: '1rem' }}>
              By logging in, you agree to our{' '}
              <a href="/terms.html" target="_blank" rel="noopener noreferrer">
                Terms and Conditions
              </a>{' '}
              and{' '}
              <a href="/privacy.html" target="_blank" rel="noopener noreferrer">
                Privacy Policy & Data Safety
              </a>.
            </p>
          </form>
        ) : null}
      </div>
    </>
  );
};

export default Login;
